import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { loadMatchHistory, summarizeMatchHistory } from '../utils/matchHistory';

const METRICS = [
  { key: 'totalGames', label: 'Total Games' },
  { key: 'wins', label: 'Wins' },
  { key: 'losses', label: 'Losses' },
  { key: 'winRate', label: 'Win Rate', suffix: '%' },
];

function formatCompletedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export default function Stats() {
  const [matches] = useState(() => loadMatchHistory());
  const summary = useMemo(() => summarizeMatchHistory(matches), [matches]);

  return (
    <div className="container py-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-2 text-3xl font-display font-bold">Statistics</h1>
        <p className="mb-8 text-muted-foreground">
          Results completed in this browser are stored locally on this device.
        </p>

        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {METRICS.map((metric) => (
            <Card
              key={metric.key}
              className="border border-border/50 bg-card/50 backdrop-blur-sm"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{metric.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <p
                  className="text-3xl font-bold text-primary"
                  data-testid={`stats-${metric.key}`}
                >
                  {summary[metric.key]}{metric.suffix || ''}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <section
          aria-labelledby="recent-matches-title"
          className="rounded-xl border border-border/50 bg-card/50 p-6 backdrop-blur-sm"
        >
          <h2 id="recent-matches-title" className="mb-4 text-2xl font-display font-bold">
            Recent Matches
          </h2>

          {matches.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/60 px-6 py-12 text-center">
              <p className="font-semibold">No completed matches recorded</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Finish a game in this browser to start building your local history.
              </p>
            </div>
          ) : (
            <ol className="space-y-4">
              {matches.slice(0, 10).map((match) => {
                const won = match.result === 'win';
                return (
                  <li
                    key={match.id}
                    className="flex items-center justify-between gap-4 rounded-lg border border-border/30 bg-background/30 p-4"
                    data-testid="stats-match"
                  >
                    <div>
                      <h3 className="font-bold">
                        {won ? 'Won' : 'Lost'} vs {match.opponent}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {match.playerScore}:{match.opponentScore}
                        {' · '}
                        {formatCompletedAt(match.completedAt)}
                        {match.deckName ? ` · ${match.deckName}` : ''}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-sm ${
                        won
                          ? 'bg-green-500/20 text-green-500'
                          : 'bg-red-500/20 text-red-500'
                      }`}
                    >
                      {won ? 'Win' : 'Loss'}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
